import { useEffect, useMemo, useRef, useState } from "react";
import {
  App as AntdApp,
  Alert,
  Button,
  Card,
  Input,
  InputNumber,
  Select,
  Space,
  Spin,
  Steps,
  Switch,
  Table,
  Tag,
  Tooltip,
} from "antd";
import {
  ArrowDownOutlined,
  ArrowLeftOutlined,
  ArrowUpOutlined,
  CheckOutlined,
  ExclamationCircleOutlined,
  PlusOutlined,
  SaveOutlined,
  SearchOutlined,
  ThunderboltOutlined,
} from "@ant-design/icons";
import { useLocation, useNavigate, useParams } from "react-router";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";

import { apiClient } from "../../services/api/apiClient";
import { usePageTitle } from "../../hooks";
import { PageHeader } from "../../components/common/PageHeader";

interface AssignableFarm {
  id: number;
  name: string;
  total_surface: number;
  producer_id: number | null;
  producer_name: string | null;
  province_id: number | null;
  province_name: string | null;
  town_id: number | null;
  town_name: string | null;
  last_audit_id: number | null;
  // Best-available date for the "Última auditoría" column:
  // Audit.start_date when set, otherwise Audit.created.date(). Null
  // only when the farm has never been audited.
  last_audit_date: string | null;
  // Whether `last_audit_date` came from a really-started audit
  // (true) or is a fallback to the planned audit's `created` (false).
  last_audit_date_is_started?: boolean;
  last_audit_type_id: number | null;
  last_audit_type_name: string | null;
  has_updates_since_last_audit: boolean;
  has_updates_reason?: "manual" | "plots" | null;
  needs_full_reaudit?: boolean;
  already_in_planning: boolean;
  suggested_audit_type_id: number | null;
  suggested_audit_type_name: string | null;
}

interface AuditTypeRow {
  id: number;
  name: string;
}

// Row that lives in the right-hand "selected" table. Extends the
// farm shape with the audit_type the user wants for that farm.
// `row_key` is the table's actual rowKey so the same farm can appear
// twice (one Aforo + one Completa) without React duplicate-key
// warnings or selection bugs.
interface SelectedFarm extends AssignableFarm {
  audit_type_id: number | null;
  row_key: string;
}

const fmtSurface = (n: number | null | undefined) =>
  new Intl.NumberFormat("es-ES", { maximumFractionDigits: 1 }).format(n ?? 0);

const fmtDate = (s: string | null | undefined) =>
  s ? dayjs(s).format("DD/MM/YYYY") : "—";

export const PlanningAssignAudits = () => {
  const { t } = useTranslation();
  const { id: idParam } = useParams<{ id?: string }>();
  const planningId = Number(idParam);
  const navigate = useNavigate();
  const location = useLocation();
  const { message: notify, modal } = AntdApp.useApp();
  usePageTitle(t("planning.assign_audits", "Asignar auditorías"));

  // Show the 2-step wizard header when this page is reached straight
  // after creating the planning (`?from=create`) — otherwise we hide
  // the Steps because the user came from the planning detail and the
  // standalone screen would look odd with a "Step 2/2" header.
  const isCreationStep = new URLSearchParams(location.search).get("from") === "create";

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [farms, setFarms] = useState<AssignableFarm[]>([]);
  const [auditTypes, setAuditTypes] = useState<AuditTypeRow[]>([]);
  const [selected, setSelected] = useState<SelectedFarm[]>([]);
  const [searchLeft, setSearchLeft] = useState("");
  const [searchRight, setSearchRight] = useState("");
  // Multi-selección de provincias para la tabla "Fincas disponibles".
  // Vacío = no filtramos por provincia. Las opciones se calculan a
  // partir de las fincas asignables (las que ya están en la
  // planificación se excluyen para no ofrecer provincias que no van a
  // aparecer en la tabla).
  const [provinceFilter, setProvinceFilter] = useState<number[]>([]);
  // Toggle "Mostrar todas": cuando está activo se desactiva la
  // paginación (pagination=false) y se renderizan todas las filas. Útil
  // sobre todo cuando hay pocas provincias y conviene ver el conjunto
  // completo de un golpe sin pelearse con el paginador.
  const [leftShowAll, setLeftShowAll] = useState(false);
  const [leftPage, setLeftPage] = useState(1);
  const [leftPageSize, setLeftPageSize] = useState(50);
  // Rango de superficie (ha). null en un extremo = sin límite por ese
  // lado. Permite, por ejemplo, "≥ 5 ha" o "entre 5 y 20 ha" sin tener
  // que escribir ambos campos.
  const [surfaceMin, setSurfaceMin] = useState<number | null>(null);
  const [surfaceMax, setSurfaceMax] = useState<number | null>(null);
  // Filas marcadas con checkbox en la tabla de la izquierda. Se llevan
  // a la tabla de la derecha como bloque cuando el usuario pulsa
  // "Asignar marcadas". Mantenemos el array para poder controlar la
  // selección desde fuera (limpiarla tras asignar).
  const [checkedLeftIds, setCheckedLeftIds] = useState<number[]>([]);
  // Filtro lateral activado al pulsar el icono de aviso en una fila de
  // la tabla de asignadas: muestra arriba sólo las fincas pendientes
  // del productor concreto para facilitar añadirlas en bloque. null =
  // sin filtro por productor.
  const [producerFilter, setProducerFilter] = useState<number | null>(null);
  // Ref al wrapper de la tabla izquierda para hacer scroll suave hasta
  // ella cuando el usuario activa el filtro de productor desde abajo.
  const leftCardRef = useRef<HTMLDivElement | null>(null);
  // Monotonic counter for unique `row_key`s on the right-hand table.
  // Stays in a ref because we only ever read/increment imperatively
  // and don't want re-renders when it changes.
  const rowKeyCounter = useRef(0);
  const nextRowKey = () => `r${++rowKeyCounter.current}`;

  // Initial load: assignable farms + audit-type catalogue (the
  // catalogue feeds the per-row Select on the right-hand table).
  useEffect(() => {
    if (!Number.isFinite(planningId)) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [farmsRes, typesRes] = await Promise.all([
          apiClient.get<AssignableFarm[]>(
            `/planning-groups/${planningId}/assignable-farms/`,
          ),
          apiClient.get(`/audit-types/?page_size=100`),
        ]);
        if (cancelled) return;
        setFarms(farmsRes.data || []);
        const list =
          typesRes.data?.results ??
          (Array.isArray(typesRes.data) ? typesRes.data : []);
        setAuditTypes(list as AuditTypeRow[]);
      } catch (e: any) {
        if (!cancelled) {
          notify.error(
            t(
              "planning.assign_audits_load_error",
              "No se pudieron cargar las fincas asignables.",
            ),
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [planningId]);

  // Set of farm ids currently on the right-hand table — used to hide
  // them from the left one and disable double-add.
  const selectedIdSet = useMemo(
    () => new Set(selected.map((s) => s.id)),
    [selected],
  );

  // Reference audit types resolved by name. The backend uses the same
  // "startswith" heuristic so admins can seed variants ("Completa",
  // "Completa - X", "Aforo"). Used to enable the per-row
  // "+ Completa" duplicate action when the row is currently Aforo.
  const completeType = useMemo(
    () => auditTypes.find((a) => /^complet/i.test(a.name || "")),
    [auditTypes],
  );
  const aforoType = useMemo(
    () => auditTypes.find((a) => /^aforo/i.test(a.name || "")),
    [auditTypes],
  );

  // Farms returned by the backend but already part of this planning
  // (they're hidden from the assignable list). We surface the count
  // in a notice + the names of any "marked-for-reaudit" ones so the
  // user knows the farm exists but is already assigned.
  const alreadyInPlanning = useMemo(
    () => farms.filter((f) => f.already_in_planning),
    [farms],
  );
  const alreadyMarkedForReaudit = useMemo(
    () => alreadyInPlanning.filter((f) => f.needs_full_reaudit),
    [alreadyInPlanning],
  );

  // Opciones del selector de provincias: derivadas de las fincas
  // asignables (las que NO están ya en la planificación). Limitarlo a
  // las que aparecerán en la tabla evita ofrecer provincias muertas
  // que no tendrían resultado posible.
  const provinceOptions = useMemo(() => {
    const seen = new Map<number, string>();
    for (const f of farms) {
      if (f.already_in_planning) continue;
      if (f.province_id == null) continue;
      if (!seen.has(f.province_id)) {
        seen.set(f.province_id, f.province_name || "—");
      }
    }
    return Array.from(seen.entries())
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [farms]);

  // Left table data: every active farm in the season's portfolio
  // that isn't already assigned in this planning AND isn't currently
  // staged on the right side. Filtered by the left search box and por
  // las provincias seleccionadas.
  const leftRows = useMemo(() => {
    const q = searchLeft.trim().toLowerCase();
    const provinceSet =
      provinceFilter.length > 0 ? new Set(provinceFilter) : null;
    return farms.filter((f) => {
      if (f.already_in_planning) return false;
      if (selectedIdSet.has(f.id)) return false;
      if (producerFilter != null && f.producer_id !== producerFilter) {
        return false;
      }
      if (provinceSet && (f.province_id == null || !provinceSet.has(f.province_id))) {
        return false;
      }
      const surface = f.total_surface ?? 0;
      if (surfaceMin != null && surface < surfaceMin) return false;
      if (surfaceMax != null && surface > surfaceMax) return false;
      if (!q) return true;
      return (
        (f.producer_name || "").toLowerCase().includes(q) ||
        (f.name || "").toLowerCase().includes(q) ||
        (f.province_name || "").toLowerCase().includes(q) ||
        (f.town_name || "").toLowerCase().includes(q)
      );
    });
  }, [farms, selectedIdSet, searchLeft, provinceFilter, surfaceMin, surfaceMax, producerFilter]);

  // Nombre del productor para pintar en el chip del filtro lateral.
  // Lo busco en `farms` porque puede que el productor ya no esté en
  // `selected` (si el usuario quita la finca de la derecha mientras el
  // filtro sigue activo).
  const producerFilterName = useMemo(() => {
    if (producerFilter == null) return null;
    const match =
      farms.find((f) => f.producer_id === producerFilter) ||
      selected.find((s) => s.producer_id === producerFilter);
    return match?.producer_name ?? `#${producerFilter}`;
  }, [producerFilter, farms, selected]);

  // Activar el filtro desde el icono de aviso: muestra arriba sólo las
  // fincas pendientes del productor, limpia el resto de filtros para
  // no esconder ninguna por error, y hace scroll suave a la tabla de
  // arriba.
  const focusPendingFarmsOf = (producerId: number) => {
    setProducerFilter(producerId);
    setSearchLeft("");
    setProvinceFilter([]);
    setSurfaceMin(null);
    setSurfaceMax(null);
    setLeftShowAll(true);
    requestAnimationFrame(() => {
      leftCardRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  // Cuando cambian los filtros (búsqueda o provincia), volvemos a la
  // primera página: si no, la tabla puede quedarse mostrando una
  // página vacía con el filtro nuevo.
  useEffect(() => {
    setLeftPage(1);
  }, [searchLeft, provinceFilter, surfaceMin, surfaceMax, producerFilter]);

  // Map producer_id → nº de fincas suyas que siguen en "Fincas
  // disponibles" (es decir, asignables pero todavía no asignadas en
  // esta sesión). Si un productor tiene fincas a medio asignar, el
  // contador > 0 y resaltamos la fila correspondiente en la tabla de
  // la derecha como aviso. Las `already_in_planning` no cuentan: ya
  // están dentro de la planificación.
  const unassignedCountByProducer = useMemo(() => {
    const counts = new Map<number, number>();
    for (const f of farms) {
      if (f.already_in_planning) continue;
      if (selectedIdSet.has(f.id)) continue;
      if (f.producer_id == null) continue;
      counts.set(f.producer_id, (counts.get(f.producer_id) ?? 0) + 1);
    }
    return counts;
  }, [farms, selectedIdSet]);

  const rightRows = useMemo(() => {
    const q = searchRight.trim().toLowerCase();
    if (!q) return selected;
    return selected.filter(
      (s) =>
        (s.producer_name || "").toLowerCase().includes(q) ||
        (s.name || "").toLowerCase().includes(q) ||
        (s.province_name || "").toLowerCase().includes(q) ||
        (s.town_name || "").toLowerCase().includes(q),
    );
  }, [selected, searchRight]);

  // Bulk move: lleva todas las fincas marcadas con checkbox a la tabla
  // de la derecha. Las que ya estuvieran allí se ignoran (idempotente).
  // El audit_type por defecto viene del hint del backend (Completa para
  // las que no se han auditado nunca, Aforo en re-auditorías).
  const assignChecked = () => {
    if (checkedLeftIds.length === 0) return;
    const byId = new Map(farms.map((f) => [f.id, f]));
    setSelected((prev) => {
      const have = new Set(prev.map((s) => s.id));
      const adds: SelectedFarm[] = [];
      for (const id of checkedLeftIds) {
        if (have.has(id)) continue;
        const farm = byId.get(id);
        if (!farm) continue;
        adds.push({
          ...farm,
          audit_type_id: farm.suggested_audit_type_id ?? null,
          row_key: nextRowKey(),
        });
      }
      return adds.length > 0 ? [...prev, ...adds] : prev;
    });
    setCheckedLeftIds([]);
  };

  // Move a farm back from the bottom to the top. Operates on the
  // local `row_key` (not farm id) so we only remove the duplicate the
  // user clicked, not every row for that farm.
  const unassignRow = (rowKey: string) => {
    setSelected((prev) => prev.filter((s) => s.row_key !== rowKey));
  };

  const updateRowAuditType = (rowKey: string, auditTypeId: number) => {
    setSelected((prev) =>
      prev.map((s) =>
        s.row_key === rowKey ? { ...s, audit_type_id: auditTypeId } : s,
      ),
    );
  };

  // Add a second row for the same farm with audit_type = Completa.
  // Triggered from the per-row "+ Completa" button on Aforo rows so
  // the user can plan an Aforo AND a full Completa for the same farm
  // within this planning. No-op when a Completa row for this farm
  // already exists (and we tell the user why).
  const addCompleteFor = (existing: SelectedFarm) => {
    if (!completeType) return;
    const alreadyHasCompleta = selected.some(
      (s) => s.id === existing.id && s.audit_type_id === completeType.id,
    );
    if (alreadyHasCompleta) {
      notify.info(
        t(
          "planning.assign_audits_already_has_completa",
          "Esta finca ya tiene una auditoría Completa en la asignación.",
        ),
      );
      return;
    }
    setSelected((prev) => [
      ...prev,
      {
        ...existing,
        row_key: nextRowKey(),
        audit_type_id: completeType.id,
      },
    ]);
  };

  const validateAndSubmit = async () => {
    if (selected.length === 0) {
      notify.warning(
        t("planning.assign_audits_empty", "Selecciona al menos una finca."),
      );
      return;
    }
    const missingType = selected.filter((s) => !s.audit_type_id);
    if (missingType.length > 0) {
      notify.warning(
        t(
          "planning.assign_audits_missing_type",
          "Hay {{count}} fincas sin tipo de auditoría asignado.",
          { count: missingType.length },
        ),
      );
      return;
    }
    modal.confirm({
      title: t("planning.assign_audits_confirm_title", "Crear auditorías"),
      content: t(
        "planning.assign_audits_confirm_body",
        "Se crearán {{count}} auditorías en estado Planificada.",
        { count: selected.length },
      ),
      okText: t("common.save", "Guardar"),
      cancelText: t("common.cancel", "Cancelar"),
      onOk: async () => {
        setSaving(true);
        try {
          const res = await apiClient.post(
            `/planning-groups/${planningId}/bulk-create-audits/`,
            {
              items: selected.map((s) => ({
                farm_id: s.id,
                audit_type_id: s.audit_type_id,
              })),
            },
          );
          const data = res.data;
          notify.success(
            t(
              "planning.assign_audits_success",
              "{{count}} auditorías creadas",
              { count: data.created_count ?? 0 },
            ),
          );
          if (Array.isArray(data.errors) && data.errors.length > 0) {
            notify.warning(
              t(
                "planning.assign_audits_partial",
                "{{count}} fincas no se pudieron asignar.",
                { count: data.errors.length },
              ),
            );
          }
          navigate(`/planning-groups/show/${planningId}`);
        } catch (e: any) {
          notify.error(
            e?.response?.data?.error ||
              t(
                "planning.assign_audits_error",
                "No se pudieron crear las auditorías.",
              ),
          );
        } finally {
          setSaving(false);
        }
      },
    });
  };

  const leftColumns = [
    {
      title: t("audit.producer", "Productor"),
      dataIndex: "producer_name",
      key: "producer_name",
      sorter: (a: AssignableFarm, b: AssignableFarm) =>
        (a.producer_name || "").localeCompare(b.producer_name || ""),
      render: (v: string | null) => v || "—",
    },
    {
      title: t("audit.farm", "Finca"),
      dataIndex: "name",
      key: "name",
      sorter: (a: AssignableFarm, b: AssignableFarm) =>
        (a.name || "").localeCompare(b.name || ""),
    },
    {
      title: t("audit.surface", "Superficie (ha)"),
      dataIndex: "total_surface",
      key: "total_surface",
      align: "right" as const,
      width: 130,
      sorter: (a: AssignableFarm, b: AssignableFarm) =>
        (a.total_surface || 0) - (b.total_surface || 0),
      render: (v: number) => fmtSurface(v),
    },
    {
      title: t("audit.province", "Provincia"),
      dataIndex: "province_name",
      key: "province_name",
      width: 140,
      sorter: (a: AssignableFarm, b: AssignableFarm) =>
        (a.province_name || "").localeCompare(b.province_name || ""),
      render: (v: string | null) => v || "—",
    },
    {
      title: t("audit.town", "Municipio"),
      dataIndex: "town_name",
      key: "town_name",
      width: 140,
      render: (v: string | null) => v || "—",
    },
    {
      title: t("planning.last_audit_date", "Última auditoría"),
      dataIndex: "last_audit_date",
      key: "last_audit_date",
      width: 150,
      sorter: (a: AssignableFarm, b: AssignableFarm) => {
        const av = a.last_audit_date ? new Date(a.last_audit_date).getTime() : 0;
        const bv = b.last_audit_date ? new Date(b.last_audit_date).getTime() : 0;
        return av - bv;
      },
      render: (v: string | null, r: AssignableFarm) =>
        v ? (
          <Tooltip
            title={
              r.last_audit_type_name
                ? `${fmtDate(v)} · ${r.last_audit_type_name}`
                : fmtDate(v)
            }
          >
            {fmtDate(v)}
          </Tooltip>
        ) : (
          <Tag color="orange">{t("planning.never_audited", "Sin auditar")}</Tag>
        ),
    },
    {
      title: (
        <Tooltip
          title={t(
            "planning.updates_since_last_audit_help",
            "Indica si la finca tiene parcelas creadas tras la última auditoría, o si un admin la ha marcado como pendiente de reauditar.",
          )}
        >
          <span>
            {t(
              "planning.updates_since_last_audit",
              "Actualizada desde última auditoría",
            )}
          </span>
        </Tooltip>
      ),
      dataIndex: "has_updates_since_last_audit",
      key: "has_updates_since_last_audit",
      width: 150,
      align: "center" as const,
      render: (v: boolean, record: AssignableFarm) => {
        // No previous audit → the comparison doesn't apply. The
        // "Sin auditar" tag in the Última auditoría column already
        // tells the user this is a brand-new farm.
        if (!record.last_audit_date) {
          return <span style={{ color: "#bfbfbf" }}>—</span>;
        }
        if (!v) {
          return <span style={{ color: "#bfbfbf" }}>—</span>;
        }
        const reason =
          (record as any).has_updates_reason === "manual"
            ? t(
                "planning.updates_reason_manual",
                "Marcada manualmente para reauditoría",
              )
            : t(
                "planning.updates_reason_plots",
                "Parcelas creadas tras la última auditoría",
              );
        return (
          <Tooltip title={reason}>
            <Tag color="gold" icon={<ThunderboltOutlined />}>
              {t("common.yes", "Sí")}
            </Tag>
          </Tooltip>
        );
      },
    },
  ];

  const rightColumns = [
    {
      title: t("audit.producer", "Productor"),
      dataIndex: "producer_name",
      key: "producer_name",
      render: (v: string | null, record: SelectedFarm) => {
        const pending =
          record.producer_id != null
            ? unassignedCountByProducer.get(record.producer_id) ?? 0
            : 0;
        return (
          <Space size={6}>
            {pending > 0 && record.producer_id != null && (
              <Tooltip
                title={t(
                  "planning.producer_has_unassigned_farms_click",
                  "Este productor tiene {{count}} fincas sin asignar a la planificación. Pulsa para verlas arriba.",
                  { count: pending },
                )}
              >
                <ExclamationCircleOutlined
                  onClick={() => focusPendingFarmsOf(record.producer_id!)}
                  style={{ color: "#faad14", cursor: "pointer" }}
                />
              </Tooltip>
            )}
            <span>{v || "—"}</span>
          </Space>
        );
      },
    },
    {
      title: t("audit.farm", "Finca"),
      dataIndex: "name",
      key: "name",
    },
    {
      title: t("audit.surface", "Sup. (ha)"),
      dataIndex: "total_surface",
      key: "total_surface",
      align: "right" as const,
      width: 110,
      render: (v: number) => fmtSurface(v),
    },
    {
      title: t("audit.province", "Provincia"),
      dataIndex: "province_name",
      key: "province_name",
      width: 140,
      render: (v: string | null) => v || "—",
    },
    {
      title: t("audit.town", "Municipio"),
      dataIndex: "town_name",
      key: "town_name",
      width: 140,
      render: (v: string | null) => v || "—",
    },
    {
      title: t("planning.last_audit_date", "Última auditoría"),
      dataIndex: "last_audit_date",
      key: "last_audit_date",
      width: 140,
      render: (v: string | null) =>
        v ? fmtDate(v) : (
          <Tag color="orange">{t("planning.never_audited", "Sin auditar")}</Tag>
        ),
    },
    {
      title: t("audit.type", "Tipo"),
      key: "audit_type",
      width: 200,
      render: (_: unknown, record: SelectedFarm) => (
        <Select
          value={record.audit_type_id ?? undefined}
          onChange={(v) => updateRowAuditType(record.row_key, v)}
          options={auditTypes.map((a) => ({ value: a.id, label: a.name }))}
          style={{ width: "100%" }}
          placeholder={t("audit.type", "Tipo")}
          showSearch
          optionFilterProp="label"
        />
      ),
    },
    {
      title: t("common.actions", "Acciones"),
      key: "actions",
      width: 110,
      fixed: "right" as const,
      align: "center" as const,
      render: (_: unknown, record: SelectedFarm) => {
        // Show "+ Completa" only when this row is currently an Aforo:
        // the user explicitly asked for the action to duplicate an
        // Aforo as Completa, and on other types it would just be
        // noise.
        const isAforo =
          aforoType != null && record.audit_type_id === aforoType.id;
        const canAddCompleta = isAforo && completeType != null;
        return (
          <Space size={4} wrap>
            {canAddCompleta && (
              <Tooltip
                title={t(
                  "planning.add_completa_for_farm",
                  "Crear también una auditoría Completa para esta finca",
                )}
              >
                <Button
                  size="small"
                  icon={<PlusOutlined />}
                  onClick={() => addCompleteFor(record)}
                  aria-label={t("planning.add_completa", "+ Completa")}
                />
              </Tooltip>
            )}
            <Tooltip title={t("planning.unassign", "Quitar")}>
              <Button
                size="small"
                icon={<ArrowUpOutlined />}
                onClick={() => unassignRow(record.row_key)}
                aria-label={t("planning.unassign", "Quitar")}
              />
            </Tooltip>
          </Space>
        );
      },
    },
  ];

  return (
    <Card>
      {isCreationStep && (
        <Steps current={1} style={{ marginBottom: 24 }}>
          <Steps.Step
            title={t("planning.basic_info", "Datos básicos")}
          />
          <Steps.Step
            title={t("planning.assign_audits", "Asignar auditorías")}
          />
        </Steps>
      )}
      <PageHeader
        icon={<CheckOutlined />}
        accent="#FA8C16"
        title={t("planning.assign_audits", "Asignar auditorías")}
        subtitle={t(
          "planning.assign_audits_subtitle",
          "Selecciona fincas de la cartera para crear auditorías en esta planificación. El tipo por defecto es Completa la primera vez y Aforo en re-auditorías.",
        )}
        extra={
          <Space>
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate(`/planning-groups/show/${planningId}`)}
              style={{ borderRadius: 999, paddingInline: 16 }}
            >
              {t("buttons.back", "Volver")}
            </Button>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              loading={saving}
              onClick={validateAndSubmit}
              disabled={selected.length === 0}
              style={{ borderRadius: 999, paddingInline: 16 }}
            >
              {t("common.save", "Guardar")}
              {selected.length > 0 ? ` (${selected.length})` : ""}
            </Button>
          </Space>
        }
      />

      {loading ? (
        <div style={{ padding: 64, textAlign: "center" }}>
          <Spin size="large" />
        </div>
      ) : (
        <Space direction="vertical" size={16} style={{ width: "100%" }}>
          {alreadyMarkedForReaudit.length > 0 && (
            <Alert
              type="warning"
              showIcon
              message={t(
                "planning.reaudit_already_assigned_title",
                "Hay fincas marcadas para reauditar ya asignadas a esta planificación",
              )}
              description={
                <div>
                  {t(
                    "planning.reaudit_already_assigned_body",
                    "No aparecen en «Fincas disponibles» porque ya tienen una auditoría en esta planificación. Edita la auditoría existente para cambiar su tipo a Completa, o quítala primero si quieres re-asignarla.",
                  )}
                  <ul style={{ marginTop: 6, paddingLeft: 18 }}>
                    {alreadyMarkedForReaudit.slice(0, 10).map((f) => (
                      <li key={f.id}>
                        <strong>{f.producer_name ?? "?"}</strong> · {f.name}
                      </li>
                    ))}
                    {alreadyMarkedForReaudit.length > 10 && (
                      <li>
                        … {alreadyMarkedForReaudit.length - 10}{" "}
                        {t("common.more", "más")}
                      </li>
                    )}
                  </ul>
                </div>
              }
            />
          )}
          {alreadyInPlanning.length > 0 && (
            <Alert
              type="info"
              showIcon
              message={t(
                "planning.farms_already_assigned",
                "{{count}} fincas de la cartera ya están asignadas a esta planificación y no aparecen en la lista de disponibles.",
                { count: alreadyInPlanning.length },
              )}
            />
          )}
          <div ref={leftCardRef}>
          <Card
            type="inner"
            style={{ width: "100%" }}
            title={t("planning.assignable_farms", "Fincas disponibles")}
            extra={
              <Space wrap>
                <Select
                  mode="multiple"
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  placeholder={t(
                    "planning.filter_by_province",
                    "Filtrar por provincia",
                  )}
                  value={provinceFilter}
                  onChange={(v) => setProvinceFilter(v as number[])}
                  options={provinceOptions}
                  maxTagCount="responsive"
                  style={{ minWidth: 260 }}
                  disabled={provinceOptions.length === 0}
                />
                <Input
                  allowClear
                  prefix={<SearchOutlined />}
                  placeholder={t(
                    "planning.search_farm",
                    "Buscar finca, productor, provincia…",
                  )}
                  value={searchLeft}
                  onChange={(e) => setSearchLeft(e.target.value)}
                  style={{ width: 260 }}
                />
                <Space size={4} align="center">
                  <span style={{ color: "rgba(0,0,0,0.65)" }}>
                    {t("planning.surface_range", "Sup. (ha)")}
                  </span>
                  <InputNumber
                    placeholder={t("common.min", "Mín")}
                    value={surfaceMin}
                    onChange={(v) =>
                      setSurfaceMin(typeof v === "number" ? v : null)
                    }
                    min={0}
                    step={0.5}
                    style={{ width: 90 }}
                  />
                  <span style={{ color: "rgba(0,0,0,0.45)" }}>—</span>
                  <InputNumber
                    placeholder={t("common.max", "Máx")}
                    value={surfaceMax}
                    onChange={(v) =>
                      setSurfaceMax(typeof v === "number" ? v : null)
                    }
                    min={0}
                    step={0.5}
                    style={{ width: 90 }}
                  />
                </Space>
                <Space size={6}>
                  <Switch
                    checked={leftShowAll}
                    onChange={setLeftShowAll}
                    size="small"
                  />
                  <span>
                    {t("planning.show_all", "Mostrar todas")}
                    {leftShowAll ? ` (${leftRows.length})` : ""}
                  </span>
                </Space>
              </Space>
            }
          >
            {/* Barra de acción para el bulk-assign. Vive sobre la tabla
                en vez de dentro del extra de la Card porque el extra
                ya está cargado con filtros y el contador "X marcadas"
                tiene más sentido cerca de la propia tabla. */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 8,
                gap: 8,
                flexWrap: "wrap",
              }}
            >
              <Space size={8} wrap>
                <span style={{ color: "rgba(0,0,0,0.65)" }}>
                  {t(
                    "planning.checked_count",
                    "{{count}} marcadas",
                    { count: checkedLeftIds.length },
                  )}
                </span>
                {producerFilter != null && (
                  <Tag
                    color="gold"
                    closable
                    onClose={() => setProducerFilter(null)}
                  >
                    {t(
                      "planning.filtering_by_producer",
                      "Filtrando por: {{name}}",
                      { name: producerFilterName ?? "" },
                    )}
                  </Tag>
                )}
              </Space>
              <Button
                type="primary"
                icon={<ArrowDownOutlined />}
                disabled={checkedLeftIds.length === 0}
                onClick={assignChecked}
              >
                {t("planning.assign_checked", "Asignar marcadas")}
                {checkedLeftIds.length > 0 ? ` (${checkedLeftIds.length})` : ""}
              </Button>
            </div>
            <Table
              size="small"
              rowKey="id"
              dataSource={leftRows}
              columns={leftColumns}
              rowSelection={{
                selectedRowKeys: checkedLeftIds,
                onChange: (keys) => setCheckedLeftIds(keys as number[]),
                // Mantenemos las marcas aunque la fila salga del
                // dataSource por un filtro/paginación: si el usuario
                // marcó 30 fincas y luego escribe en el buscador, no
                // queremos que se le pierdan las que ya no se ven.
                preserveSelectedRowKeys: true,
              }}
              pagination={
                leftShowAll
                  ? false
                  : {
                      current: leftPage,
                      pageSize: leftPageSize,
                      showSizeChanger: true,
                      pageSizeOptions: ["10", "20", "50", "100"],
                      showTotal: (total) =>
                        t("planning.pagination_total", "{{count}} fincas", {
                          count: total,
                        }),
                      onChange: (page, size) => {
                        setLeftPage(page);
                        setLeftPageSize(size);
                      },
                    }
              }
              // No scroll.x: let the table flow to the full card
              // width; columns without an explicit `width` stretch to
              // fill the available space.
              tableLayout="auto"
              style={{ width: "100%" }}
            />
          </Card>
          </div>

          <Card
            type="inner"
            style={{ width: "100%" }}
            title={
              <Space size={6}>
                {t("planning.assigned_audits", "Auditorías asignadas")}
                <Tag color="orange">{selected.length}</Tag>
              </Space>
            }
            extra={
              <Input
                allowClear
                prefix={<SearchOutlined />}
                placeholder={t(
                  "planning.search_farm",
                  "Buscar finca, productor…",
                )}
                value={searchRight}
                onChange={(e) => setSearchRight(e.target.value)}
                style={{ width: 280 }}
              />
            }
          >
            <Table
              size="small"
              rowKey="row_key"
              dataSource={rightRows}
              columns={rightColumns}
              pagination={{ pageSize: 10, showSizeChanger: true }}
              tableLayout="auto"
              style={{ width: "100%" }}
              // Resaltamos las filas cuyo productor todavía tiene
              // fincas en "Fincas disponibles" — son productores que el
              // usuario está asignando "a medias" y conviene avisar.
              rowClassName={(record) => {
                const pending =
                  record.producer_id != null
                    ? unassignedCountByProducer.get(record.producer_id) ?? 0
                    : 0;
                return pending > 0 ? "assign-audits-row-warning" : "";
              }}
              locale={{
                emptyText: t(
                  "planning.assign_audits_empty_right",
                  "Pulsa «Asignar» en una finca para añadirla aquí.",
                ),
              }}
            />
            <style>{`
              .assign-audits-row-warning > td { background-color: #fffbe6 !important; }
              .assign-audits-row-warning:hover > td { background-color: #fff7d4 !important; }
            `}</style>
          </Card>
        </Space>
      )}
    </Card>
  );
};

export default PlanningAssignAudits;
